import * as React from 'react'
import { Text, View } from '../Themed'
import GlobalStyle, { largeTextSize, xlTextSize } from '../../constants/GlobalStyle'
import LineItem from './LineItem'
import PricesView from './PricesView'
import Colors from '../../constants/Colors'

interface AggregatedItem {
	[key: string]: [number, string, ...React.ReactNode[]]
}

interface RestaurantOrderProps {
	restaurantName: string
	restaurantTotal: number
	menuItems: React.ReactNode[]
	isCheckout: boolean
}

const RestaurantOrder: React.FC<RestaurantOrderProps> = ({
	restaurantName,
	restaurantTotal,
	menuItems,
	isCheckout,
}) => (
	<View>
		<RestaurantHeader name={restaurantName} isCheckout={isCheckout} />
		{menuItems}
	</View>
)

export function DisplayOrder({
	items,
	prices,
	isCheckout,
	isConfirmed,
	navigation,
	deletePopup,
	loadedData,
	trackingURL,
}) {
	const aggregatedItems = innerObjects(
		items,
		isCheckout,
		isConfirmed,
		navigation,
		deletePopup,
		loadedData
	) as AggregatedItem

	const restaurantOrders = Object.entries(aggregatedItems).map(([restaurantId, value]) => {
		const [restaurantTotal, restaurantName, ...menuItems] = value
		return (
			<RestaurantOrder
				key={restaurantId}
				restaurantName={restaurantName}
				restaurantTotal={restaurantTotal}
				menuItems={menuItems}
				isCheckout={isCheckout}
			/>
		)
	})

	return (
		<>
			{restaurantOrders}
			{!isCheckout && <PricesView key={`prices_${prices.total}`} prices={prices} />}
		</>
	)
}

//aggregate all items under their specific restaurant
//agg[key][0] = restaurant's total
//agg[key][1] = restaurant heading
//agg[key][2] = array of items
function innerObjects(items, isCheckout, isConfirmed, navigation, deletePopup, loadedData) {
	const agg = {}

	const sortedItems = [...items].sort((a, b) => {
		const timeA = a.timeAdded || Number.MAX_SAFE_INTEGER // If timeAdded is missing, place it at the end
		const timeB = b.timeAdded || Number.MAX_SAFE_INTEGER
		return timeA - timeB
	})

	sortedItems.forEach(item => {
		const curr = agg[item.rId]
		let num = (isCheckout ? item.ppu / 100 : Number(item.total / 100)) * item.qty
		let modAmount = 0
		item.mods.forEach(mod => {
			const modPrice = isCheckout ? mod.priceMoney.amount / 100 : Number(mod.price)
			modAmount += modPrice
		})
		num += modAmount

		if (curr) {
			curr[0] += num
			curr.push(
				<LineItem
					key={'LINE_ITEM_' + item.id + item.idempotencyKey}
					item={item}
					navigation={navigation}
					isCheckout={isCheckout}
					isConfirmed={isConfirmed}
					deletePopup={deletePopup}
					loadedData={loadedData}
				/>
			)
			agg[item.rId] = curr
		} else if (loadedData[item.rId]) {
			agg[item.rId] = [num]
			agg[item.rId].push(loadedData[item.rId].name)

			agg[item.rId].push([
				<LineItem
					key={'LINE_ITEM_' + item.id + item.idempotencyKey}
					item={item}
					navigation={navigation}
					isCheckout={isCheckout}
					isConfirmed={isConfirmed}
					deletePopup={deletePopup}
					loadedData={loadedData}
				/>,
			])
		}
	})

	return agg
}

//display the restaurant heading above the items in the order from that restaurant
function RestaurantHeader({ name, isCheckout }) {
	isCheckout = false
	return (
		<View>
			<Text
				style={{
					paddingHorizontal: 15,
					fontSize: 22,
					marginBottom: 10,
					fontWeight: '200',
					color: Colors.greyscale[8],
				}}
			>
				{name}
			</Text>
		</View>
	)
}
